import {
  useCallback,
  useRef,
  type KeyboardEvent,
  type MouseEvent,
} from "react";

const HOLD_DELAY = 200;
const REPEAT_INTERVAL = 300;

/**
 * Press-and-hold auto-repeat for a button — a quick press/release fires
 * `action` once; holding past `HOLD_DELAY` fires it every `REPEAT_INTERVAL`
 * until released.
 */
export const useHoldToRepeat = (action: () => void) => {
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalId = useRef<ReturnType<typeof setInterval> | null>(null);

  const start = useCallback(() => {
    holdTimer.current = setTimeout(() => {
      intervalId.current = setInterval(action, REPEAT_INTERVAL);
    }, HOLD_DELAY);
  }, [action]);

  const cancel = useCallback(() => {
    if (holdTimer.current) {
      clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
    if (intervalId.current) {
      clearInterval(intervalId.current);
      intervalId.current = null;
    }
  }, []);

  // mouseup/touchend — a hold already repeated the action, so releasing
  // just stops it; a quick press/release never started repeating, so this
  // is what fires the single click
  const end = useCallback(() => {
    const wasHolding = intervalId.current !== null;
    cancel();
    if (!wasHolding) action();
  }, [action, cancel]);

  // first (non-repeat) keydown behaves like mousedown — starts our own
  // 200ms/300ms cadence. OS auto-repeat keydowns are ignored entirely; our
  // own interval, not the OS's repeat rate, drives any further repeats.
  // preventDefault always, or the browser's derived click fires per repeat
  // and doubles up with onClick below.
  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      if (event.repeat) return;
      start();
    },
    [start],
  );

  // keyup behaves like mouseup — same "was it holding" fire-once-or-stop
  const onKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      end();
    },
    [end],
  );

  // catches non-pointer, non-keyboard activation (assistive tech,
  // programmatic .click()) via the same native-click detail===0 signal —
  // never fires for real keyboard presses since onKeyDown already
  // prevented the browser's derived click for those.
  const onClick = useCallback(
    (event: MouseEvent) => {
      if (event.detail === 0) action();
    },
    [action],
  );

  return {
    onMouseDown: start,
    onMouseUp: end,
    onMouseLeave: cancel,
    onTouchStart: start,
    onTouchEnd: end,
    onKeyDown,
    onKeyUp,
    onClick,
  };
};
